import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, API, fmtDate } from "@/lib/api";
import { toast } from "sonner";
import {
  ArrowLeft, Download, Upload, FileSpreadsheet, Database,
  FileJson, RotateCcw, CalendarClock, CheckCircle2, AlertTriangle,
  Cloud, ExternalLink, Loader2, Unlink,
} from "lucide-react";

const tabs = [
  { id: "backup", label: "Backup", icon: Database },
  { id: "drive", label: "Google Drive", icon: Cloud },
  { id: "restore", label: "Restaurar", icon: RotateCcw },
  { id: "importar", label: "Importar Planilha", icon: Upload },
  { id: "auto", label: "Automático", icon: CalendarClock },
];

export default function Dados() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [tab, setTab] = useState(params.get("drive") === "connected" ? "drive" : "backup");
  const [settings, setSettings] = useState(null);
  const [drive, setDrive] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadSettings = async () => {
    const r = await api.get("/backup/settings");
    setSettings(r.data);
  };
  const loadDrive = async () => {
    try { const r = await api.get("/drive/status"); setDrive(r.data); } catch {}
  };
  useEffect(() => {
    loadSettings();
    loadDrive();
    const driveParam = params.get("drive");
    if (driveParam === "connected") toast.success("Google Drive conectado! 🎉");
    if (driveParam === "error") {
      const msg = params.get("msg") || "Erro desconhecido";
      const friendly = {
        access_denied: "Você cancelou a autorização no Google.",
        redirect_uri_mismatch: "URL de retorno não bate com o Google Cloud. Verifique os Redirect URIs.",
        invalid_state: "Sessão expirou. Tente conectar de novo.",
        missing_code_or_state: "Google não retornou os dados esperados. Tente novamente.",
        missing_drive_scope: "Você precisa marcar a permissão do Drive.",
        token_exchange_failed: "Google recusou a troca de token. Verifique se as chaves estão corretas.",
      }[msg] || `Erro do Google: ${msg}`;
      toast.error(friendly, { duration: 8000 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const downloadBlob = async (path, filename) => {
    setBusy(true);
    try {
      const token = localStorage.getItem("cf_token");
      const res = await fetch(`${API}${path}`, { headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error("Falha no download");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = filename; a.click();
      URL.revokeObjectURL(url);
      toast.success("Backup baixado para o seu computador!");
      loadSettings();
    } catch {
      toast.error("Erro ao baixar backup");
    } finally { setBusy(false); }
  };

  const doRestore = async (file) => {
    if (!file) return;
    if (!window.confirm("⚠️ Isso vai APAGAR seus dados atuais e substituir pelo backup. Confirma?")) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/backup/restore", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Restaurado: ${r.data.restored_clientes} clientes, ${r.data.restored_vendas} vendas`);
      loadSettings();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao restaurar");
    } finally { setBusy(false); }
  };

  const importarPlanilha = async (file) => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/import/planilha", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${r.data.imported} clientes importados`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro");
    } finally { setBusy(false); }
  };

  const toggleAuto = async () => {
    const next = !settings?.auto_weekly;
    await api.put("/backup/settings", { auto_weekly: next });
    toast.success(next ? "Backup semanal ativado 🔔" : "Backup automático desligado");
    loadSettings();
  };

  const connectDrive = async () => {
    setBusy(true);
    try {
      const r = await api.get("/drive/connect");
      window.location.href = r.data.authorization_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao iniciar conexão");
      setBusy(false);
    }
  };

  const disconnectDrive = async () => {
    if (!window.confirm("Desconectar o Google Drive? Os arquivos já enviados continuam na sua conta.")) return;
    await api.post("/drive/disconnect");
    toast.success("Google Drive desconectado");
    loadDrive();
  };

  const backupNow = async () => {
    setBusy(true);
    try {
      const r = await api.post("/drive/backup-now");
      toast.success("Backup enviado pro seu Drive!");
      loadDrive();
      if (r.data.link) window.open(r.data.link, "_blank");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro no envio");
    } finally { setBusy(false); }
  };

  const toggleDriveAuto = async () => {
    const next = !drive?.auto_daily;
    try {
      await api.put("/drive/settings", { auto_daily: next });
      toast.success(next ? "Envio diário às 18h ligado 🕕" : "Envio diário desligado");
      loadDrive();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro");
    }
  };

  return (
    <div>
      <button onClick={() => nav("/")} className="text-sm text-stone-500 hover:text-stone-800 inline-flex items-center gap-1 mb-3">
        <ArrowLeft className="w-4 h-4" /> Voltar
      </button>
      <h1 className="font-display text-3xl font-extrabold tracking-tight text-stone-900 mb-1">Meus Dados</h1>
      <p className="text-sm text-stone-500 mb-4">Baixe cópias, restaure, importe e conecte o Google Drive.</p>

      <div className="flex gap-1 mb-4 overflow-x-auto no-scrollbar pb-1">
        {tabs.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            data-testid={`tab-${id}`}
            onClick={() => setTab(id)}
            className={`shrink-0 flex items-center gap-1.5 px-3 h-10 rounded-full text-sm font-bold border transition-colors ${
              tab === id
                ? "bg-[color:var(--terracotta)] text-white border-[color:var(--terracotta)]"
                : "bg-white text-stone-700 border-stone-200 hover:border-stone-400"
            }`}
          >
            <Icon className="w-4 h-4" /> {label}
          </button>
        ))}
      </div>

      {settings?.needs_backup_now && (
        <div className="carne-card p-3 mb-4 flex items-start gap-2 border-l-4" style={{ borderLeftColor: "var(--amber)" }} data-testid="backup-reminder">
          <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="text-sm text-stone-700">
            <b>Faz mais de 7 dias sem backup.</b> Baixe agora pra manter a cópia no seu PC.
          </div>
        </div>
      )}

      {tab === "backup" && (
        <div className="space-y-3">
          <div className="carne-card p-5">
            <div className="flex items-center gap-2 mb-2">
              <FileSpreadsheet className="w-5 h-5 text-emerald-700" />
              <h3 className="font-display font-bold">Baixar Excel (.xlsx)</h3>
            </div>
            <p className="text-sm text-stone-500 mb-3">
              Planilha com abas <b>Clientes</b>, <b>Vendas</b> e <b>Parcelas</b>. Abre no Excel/Google Sheets.
            </p>
            <button
              data-testid="btn-export-excel"
              onClick={() => downloadBlob("/backup/export/excel", `carne-facil-${new Date().toISOString().slice(0,10)}.xlsx`)}
              disabled={busy}
              className="btn-primary w-full"
            >
              <Download className="w-4 h-4" /> Baixar Excel
            </button>
          </div>

          <div className="carne-card p-5">
            <div className="flex items-center gap-2 mb-2">
              <FileJson className="w-5 h-5 text-[color:var(--terracotta)]" />
              <h3 className="font-display font-bold">Backup completo (.json)</h3>
            </div>
            <p className="text-sm text-stone-500 mb-3">
              Arquivo de segurança com <b>tudo</b>. Guarde no seu PC ou no pendrive — dá pra restaurar depois.
            </p>
            <button
              data-testid="btn-export-json"
              onClick={() => downloadBlob("/backup/export/json", `carne-facil-${new Date().toISOString().slice(0,10)}.json`)}
              disabled={busy}
              className="btn-primary w-full"
              style={{ background: "var(--coal)" }}
            >
              <Download className="w-4 h-4" /> Baixar backup JSON
            </button>
          </div>

          {settings?.last_backup_at && (
            <div className="text-xs text-stone-500 text-center">
              Último backup: {fmtDate(settings.last_backup_at)}
            </div>
          )}
        </div>
      )}

      {tab === "drive" && (
        <div className="space-y-3" data-testid="drive-panel">
          <div className="carne-card p-5">
            <div className="flex items-center gap-2 mb-3">
              <Cloud className="w-6 h-6 text-blue-600" />
              <h3 className="font-display text-lg font-bold">Envio automático para o Google Drive</h3>
            </div>

            {!drive?.configured && (
              <div className="text-sm bg-amber-50 border border-amber-200 text-amber-900 rounded-lg p-3">
                Google Drive ainda não configurado pelo administrador do app. Assim que as chaves da Google forem cadastradas, a conexão fica disponível aqui.
              </div>
            )}

            {drive?.configured && !drive?.connected && (
              <>
                <p className="text-sm text-stone-600 mb-4">
                  Conecte a sua conta Google e o app envia todo dia às <b>18h (Brasília)</b> uma cópia atualizada da sua caderneta para uma pasta chamada <b>"Carnê Fácil - Backups"</b> no seu Drive.
                </p>
                <button
                  data-testid="btn-drive-connect"
                  onClick={connectDrive}
                  disabled={busy}
                  className="btn-primary w-full"
                  style={{ background: "#1a73e8" }}
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Cloud className="w-4 h-4" />}
                  Conectar Google Drive
                </button>
                <p className="text-xs text-stone-500 mt-3">
                  O app só terá acesso aos arquivos que ele mesmo criar — não vê o resto do seu Drive.
                </p>
              </>
            )}

            {drive?.connected && (
              <>
                <div className="flex items-center gap-2 text-sm bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-lg p-3 mb-3">
                  <CheckCircle2 className="w-4 h-4 shrink-0" />
                  <div>Conectado como <b>{drive.email || "sua conta"}</b></div>
                </div>

                {drive.last_uploaded_at ? (
                  <div className="bg-white rounded-xl border border-stone-200 p-3 mb-3">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-stone-500">Último envio</div>
                    <div className="text-sm font-semibold text-stone-900">{fmtDate(drive.last_uploaded_at)}</div>
                    {drive.last_file_link && (
                      <a
                        data-testid="drive-last-file-link"
                        href={drive.last_file_link}
                        target="_blank"
                        rel="noreferrer"
                        className="text-xs text-blue-600 font-bold hover:underline inline-flex items-center gap-1 mt-1"
                      >
                        Abrir no Drive <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                ) : (
                  <div className="text-sm text-stone-500 mb-3">Nenhum envio ainda. Faça o primeiro agora ↓</div>
                )}

                <button
                  data-testid="btn-drive-backup-now"
                  onClick={backupNow}
                  disabled={busy}
                  className="btn-primary w-full mb-2"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                  Enviar backup agora
                </button>

                <button
                  data-testid="btn-toggle-drive-auto"
                  onClick={toggleDriveAuto}
                  className={`w-full h-12 rounded-xl font-bold flex items-center justify-between px-4 border-2 transition-colors ${
                    drive.auto_daily
                      ? "bg-emerald-50 border-emerald-400 text-emerald-800"
                      : "bg-white border-stone-300 text-stone-700"
                  }`}
                >
                  <span className="text-sm">
                    Envio automático diário às 18h
                  </span>
                  <span
                    className={`w-11 h-6 rounded-full transition-colors relative ${
                      drive.auto_daily ? "bg-emerald-500" : "bg-stone-300"
                    }`}
                  >
                    <span
                      className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-all ${
                        drive.auto_daily ? "left-5" : "left-0.5"
                      }`}
                    />
                  </span>
                </button>

                <button
                  data-testid="btn-drive-disconnect"
                  onClick={disconnectDrive}
                  className="mt-3 w-full text-sm text-rose-700 font-bold inline-flex items-center justify-center gap-1 hover:underline"
                >
                  <Unlink className="w-4 h-4" /> Desconectar Google Drive
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {tab === "restore" && (
        <div className="carne-card p-5">
          <div className="flex items-center gap-2 mb-2">
            <RotateCcw className="w-5 h-5 text-[color:var(--terracotta)]" />
            <h3 className="font-display font-bold">Restaurar de backup JSON</h3>
          </div>
          <div className="bg-rose-50 border border-rose-200 text-rose-800 text-sm rounded-lg p-3 mb-3 flex items-start gap-2">
            <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
            <div>Isso vai <b>substituir todos os seus dados atuais</b> pelos do arquivo. Faça um backup antes por garantia.</div>
          </div>
          <label className="block cursor-pointer">
            <input
              data-testid="input-restore-file"
              type="file"
              accept=".json,application/json"
              onChange={(e) => doRestore(e.target.files?.[0])}
              className="hidden"
            />
            <div className="w-full aspect-[4/2] rounded-xl border-2 border-dashed border-stone-300 bg-white flex flex-col items-center justify-center gap-2 text-stone-600 hover:border-[color:var(--terracotta)] transition-colors">
              <FileJson className="w-10 h-10 text-[color:var(--terracotta)]" strokeWidth={1.8} />
              <div className="font-display font-bold">Escolher arquivo .json</div>
              <div className="text-xs text-stone-500">Backup do Carnê Fácil</div>
            </div>
          </label>
        </div>
      )}

      {tab === "importar" && (
        <div className="carne-card p-5">
          <div className="flex items-center gap-2 mb-2">
            <Upload className="w-5 h-5 text-[color:var(--terracotta)]" />
            <h3 className="font-display font-bold">Importar planilha de clientes</h3>
          </div>
          <p className="text-sm text-stone-500 mb-3">
            Traga clientes da sua caderneta antiga (.xlsx, .xls ou .csv).
          </p>
          <label className="block cursor-pointer">
            <input
              data-testid="input-spreadsheet-file"
              type="file"
              accept=".xlsx,.xls,.csv"
              onChange={(e) => importarPlanilha(e.target.files?.[0])}
              className="hidden"
            />
            <div className="w-full aspect-[4/2] rounded-xl border-2 border-dashed border-stone-300 bg-white flex flex-col items-center justify-center gap-2 text-stone-600 hover:border-[color:var(--terracotta)] transition-colors">
              <FileSpreadsheet className="w-10 h-10 text-[color:var(--terracotta)]" strokeWidth={1.8} />
              <div className="font-display font-bold">Escolher planilha</div>
              <div className="text-xs text-stone-500">Colunas: nome (obrig.), telefone, bairro, endereço, obs.</div>
            </div>
          </label>
        </div>
      )}

      {tab === "auto" && (
        <div className="carne-card p-5">
          <div className="flex items-center gap-2 mb-2">
            <CalendarClock className="w-5 h-5 text-[color:var(--terracotta)]" />
            <h3 className="font-display font-bold">Lembrete semanal de backup</h3>
          </div>
          <p className="text-sm text-stone-500 mb-4">
            Quando ligado, o app te lembra toda semana pra baixar uma cópia local dos seus dados no PC.
          </p>
          <button
            data-testid="btn-toggle-auto"
            onClick={toggleAuto}
            className={`w-full h-14 rounded-xl font-bold flex items-center justify-between px-4 border-2 transition-colors ${
              settings?.auto_weekly
                ? "bg-emerald-50 border-emerald-400 text-emerald-800"
                : "bg-white border-stone-300 text-stone-700"
            }`}
          >
            <span>{settings?.auto_weekly ? "Ativado" : "Desligado"}</span>
            <span
              className={`w-12 h-7 rounded-full transition-colors relative ${
                settings?.auto_weekly ? "bg-emerald-500" : "bg-stone-300"
              }`}
            >
              <span
                className={`absolute top-0.5 w-6 h-6 bg-white rounded-full shadow transition-all ${
                  settings?.auto_weekly ? "left-5" : "left-0.5"
                }`}
              />
            </span>
          </button>
          {settings?.last_backup_at && (
            <div className="text-xs text-stone-500 mt-3 inline-flex items-center gap-1">
              <CheckCircle2 className="w-3 h-3 text-emerald-600" /> Último backup: {fmtDate(settings.last_backup_at)}
            </div>
          )}
          <div className="mt-5 text-xs text-stone-500 border-t border-stone-100 pt-4">
            💡 Quer backup automático <b>na nuvem sem esforço</b>? Vá na aba <b>Google Drive</b> e conecte sua conta — o app envia todo dia às 18h.
          </div>
        </div>
      )}
    </div>
  );
}
